import type { Exchange, Operation, OperationResult } from 'urql';
import { filter, makeSubject, merge, pipe, share, tap } from 'wonka';

type Priority = 'high' | 'low';

const getPriority = (operation: Operation): Priority =>
  (operation.context as Record<string, unknown>).priority === 'low'
    ? 'low'
    : 'high';

const defersLowPriority = (operation: Operation): boolean =>
  getPriority(operation) === 'high' &&
  (operation.context as Record<string, unknown>).defersLowPriority !== false;

/**
 * URQL exchange that defers low-priority operations until all in-flight
 * high-priority operations have completed.
 *
 * Tag an operation as low priority via its context:
 *   useQuery({ query, context: { priority: 'low' } })
 *   gqlClient.query(QUERY, {}, { priority: 'low' })
 *
 * All untagged operations default to 'high' priority.
 */
export const priorityExchange: Exchange =
  ({ forward }) =>
  (ops$) => {
    const shared$ = pipe(ops$, share);

    let highInFlight = 0;
    const lowQueue: Operation[] = [];
    const { source: deferred$, next: pushDeferred } = makeSubject<Operation>();
    // Track how many in-flight HIGH operations exist per key.
    // A Map<key, count> is used instead of a Set because the same key can
    // appear multiple times (e.g. requestPolicyExchange cache-and-network
    // revalidations). A Set would lose count and cause teardown mismatches.
    const inFlightHighCounts = new Map<number, number>();
    const tornDownKeys = new Set<number>();

    const flushQueue = () => {
      while (lowQueue.length > 0 && highInFlight === 0) {
        const op = lowQueue.shift();
        if (op && !tornDownKeys.has(op.key)) {
          pushDeferred(op);
        }
        if (op) tornDownKeys.delete(op.key);
      }
    };

    // High-priority operations and teardowns pass straight through.
    const high$ = pipe(
      shared$,
      filter((op: Operation) => {
        if (op.kind === 'teardown') return true;
        return getPriority(op) === 'high';
      }),
      tap((op: Operation) => {
        if (op.kind === 'teardown') {
          // Remove from lowQueue if it was queued but never forwarded
          const queueIdx = lowQueue.findIndex((o) => o.key === op.key);
          if (queueIdx !== -1) {
            lowQueue.splice(queueIdx, 1);
          } else {
            // Only track in tornDownKeys if the op might still be flushed
            tornDownKeys.add(op.key);
          }
          const count = inFlightHighCounts.get(op.key) ?? 0;
          if (count > 0) {
            if (count === 1) {
              inFlightHighCounts.delete(op.key);
            } else {
              inFlightHighCounts.set(op.key, count - 1);
            }
            highInFlight = Math.max(0, highInFlight - 1);
            if (highInFlight === 0) flushQueue();
          }
        } else if (defersLowPriority(op)) {
          highInFlight++;
          inFlightHighCounts.set(
            op.key,
            (inFlightHighCounts.get(op.key) ?? 0) + 1
          );
        }
      })
    );

    // Low-priority operations are either forwarded immediately (when nothing
    // high-priority is in-flight) or queued until the in-flight count drops to 0.
    const low$ = pipe(
      shared$,
      filter(
        (op: Operation) => op.kind !== 'teardown' && getPriority(op) === 'low'
      ),
      filter((op: Operation) => {
        if (highInFlight > 0) {
          // Clear stale teardown entries: a previous operation with the same
          // key may have passed through and been torn down. The new operation
          // supersedes that teardown.
          tornDownKeys.delete(op.key);
          lowQueue.push(op);
          return false;
        }
        return true;
      })
    );

    const forwarded$ = forward(merge([high$, low$, deferred$]));

    return pipe(
      forwarded$,
      tap((result: OperationResult) => {
        if (
          result.operation.kind !== 'teardown' &&
          defersLowPriority(result.operation)
        ) {
          const count = inFlightHighCounts.get(result.operation.key) ?? 0;
          if (count > 0) {
            if (count === 1) {
              inFlightHighCounts.delete(result.operation.key);
            } else {
              inFlightHighCounts.set(result.operation.key, count - 1);
            }
          }
          highInFlight = Math.max(0, highInFlight - 1);
          if (highInFlight === 0) {
            flushQueue();
          }
        }
      })
    );
  };
